import React, { FC, MutableRefObject, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'

import { CRMFormData, CRMServerResponse } from '../Types/Types'
import { useForm, UseFormMethods } from '../../../../../../Vendor/react-hook-form/dist/index.ie11'
import { CRMConfigurationTemplateType, CRMModalID } from '../Constants/Constants'
import { CRMContent } from './CRMContent'
import { ON_CHANGE } from '../../../../../../React/Common/Constants/HookFormEventNames'
import { EMPTY_STRING } from '../../../../../../React/Common/Constants/Separators'
import { ModalUtilsProvider } from '../../../../../../React/Common/Contexts/ModalUtilsContext'
import { DEFAULT_CME_ID } from '../../../../../../React/Common/Constants/ServerValues'
import {
    ALL_VALUE,
    ANY_VALUE_STRING_DEFAULT,
    ANY_VALUE_STRING_NUMERIC
} from '../../../../../../React/Common/Constants/DefaultValues'
import { isVarNotEmpty } from '../../../../../../NonReact/utils/GlobalFunctions'
import { AdminFilterSingleValues } from '../../../../Molecules/AdminFilters/AdminFilters'
import { useFeatureFlag } from '../../../../../../React/Common/Hooks/useFeatureFlag'
import { ENABLE_CRM_TEMPLATE_TYPES } from '../../../../../Constants/FeatureFlags'
import { ReactState } from '../../../../../../React/Common/Contexts'
import { OAuthConfig } from '../../../../../../React/Common/Components/OAuthButton/Types/Types'
import { getCRMOAuthConfiguration } from '../../../../../../NonReact/common/API/EntityAPIClient'
import { JSONResponse } from '../../../../../../NonReact/common/Types/Types'
import { displayError } from '../../../../../../React/Common/Utils/Messages'
import { mapFromServerToForm } from '../Mappers/Mappers'

type Props = {
    filters: Nullable<AdminFilterSingleValues>
    currentCRMConfiguration?: Nullable<CRMServerResponse>
    closeModal: (refresh: boolean) => void
    isViewOnly: boolean
}

export const CRM: FC<Props> = ({ currentCRMConfiguration, filters, closeModal, isViewOnly }: Props): ReactElement => {
    const isTemplateTypesEnabled: boolean = useFeatureFlag<boolean>(ENABLE_CRM_TEMPLATE_TYPES, false)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [authConfiguration, setAuthConfiguration]: ReactState<Nullable<OAuthConfig>> =
        useState<Nullable<OAuthConfig>>(null)

    // Sets the default values for the form within the Modal.
    const defaultValues: MutableRefObject<CRMFormData> = useRef({
        details: {
            description: EMPTY_STRING,
            ID: EMPTY_STRING
        },
        scopeAssignment: {
            cme: filters?.cme?.toString() ?? DEFAULT_CME_ID,
            telephonyServer: filters?.telephonyServer ?? ALL_VALUE,
            partition: filters?.partition ?? ALL_VALUE,
            groupDevice: filters?.device ?? ANY_VALUE_STRING_DEFAULT,
            filterRecursively: false
        },
        configuration: {
            crmType: ANY_VALUE_STRING_NUMERIC,
            templateType: currentCRMConfiguration?.ConfigurationType ?? CRMConfigurationTemplateType.Desktop,
            crmOptions: []
        }
    })

    // Form context.
    const methods: UseFormMethods<CRMFormData> = useForm<CRMFormData>({
        defaultValues: defaultValues.current,
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    const { reset }: UseFormMethods<CRMFormData> = methods

    // get all properties from defaultValues
    const validationFields: string[] = useMemo(
        (): string[] => Object.keys(defaultValues.current),
        [JSON.stringify(defaultValues.current)]
    )

    useEffect((): void => {
        if (isVarNotEmpty(currentCRMConfiguration)) {
            const mappedConfiguration: CRMFormData = mapFromServerToForm(currentCRMConfiguration)
            reset(mappedConfiguration)

            if (
                isTemplateTypesEnabled &&
                currentCRMConfiguration.ConfigurationType === CRMConfigurationTemplateType.Cloud
            ) {
                getCRMOAuthConfiguration(currentCRMConfiguration.CRMConfigurationID)
                    .then((response: JSONResponse<OAuthConfig>) => {
                        if (isMounted.current) {
                            setAuthConfiguration(response)
                        }
                    })
                    .catch((error: Error): void => {
                        displayError(error)
                    })
            }
        }
    }, [currentCRMConfiguration, isTemplateTypesEnabled])

    return (
        <ModalUtilsProvider
            initialTabID={CRMModalID.CRMModalDetails}
            validationFields={validationFields}
            closeModal={closeModal}
            methods={methods as unknown as UseFormMethods}
        >
            <CRMContent
                currentCRMConfiguration={currentCRMConfiguration}
                authConfiguration={authConfiguration}
                closeModal={closeModal}
                methods={methods}
                viewOnly={isViewOnly}
            />
        </ModalUtilsProvider>
    )
}
